// ProgrammesScreen V20: Phase 3 visual redesign — token migration, Outfit font, Phosphor icons, light headers
import { useState, useEffect, useRef } from "react";
import { Plus, X, Trash, PencilSimple, FloppyDisk, CalendarBlank, CaretDown, CaretRight, Check, PaperPlaneTilt, Sparkle, Target } from "@phosphor-icons/react";
import { T } from "../../themes";
import { fmtDuration } from "../../constants";
import { llmFetch, LLM_MODEL_HEAVY, LLM_MODEL_LIGHT, cleanAIResponse, AI_SAFETY_LAW, WORKOUT_SAFETY_LAW, INJURY_LAW } from "../../llm";
import {
  getActiveProgrammeProgress,
  ComicCard, SectionLabel, Btn, Field, Spinner, Tag
} from "../../workoutHelpers";
import AppHeader from "../../components/AppHeader";

// TODO: LABEL_COLOURS uses hardcoded pastel hex — no semantic T token match yet
const LABEL_COLOURS = {
  push:     { bg: '#dbeafe', text: '#1e40af' },
  pull:     { bg: '#ede9fe', text: '#6d28d9' },
  legs:     { bg: '#dcfce7', text: '#15803d' },
  full:     { bg: '#fef9c3', text: '#854d0e' },
  mobility: { bg: '#f0fdf4', text: '#166534' },
};
function getLabelColour(label) {
  const l = (label || "").toLowerCase();
  if (l.includes("push") || l.includes("upper") || l.includes("chest")) return LABEL_COLOURS.push;
  if (l.includes("pull") || l.includes("back")) return LABEL_COLOURS.pull;
  if (l.includes("leg") || l.includes("lower")) return LABEL_COLOURS.legs;
  if (l.includes("full")) return LABEL_COLOURS.full;
  if (l.includes("mobility") || l.includes("athletic") || l.includes("cardio")) return LABEL_COLOURS.mobility;
  if (l.includes("rest")) return { bg: T.surfaceLight, text: T.inkMuted };
  return { bg: T.primary + '22', text: T.primary };
}

// Shared header — delegates to AppHeader, threading screen-level props
function PlansHeader({ title, subtitle, onBack, rightContent, darkMode, setDarkMode, profile }) {
  return (
    <AppHeader
      title={title}
      subtitle={subtitle}
      currentApp="lift"
      showBackButton={!!onBack}
      onBackPress={onBack}
      rightContent={rightContent}
      darkMode={darkMode}
      onToggleDarkMode={setDarkMode ? ()=>setDarkMode(d=>!d) : undefined}
      username={profile?.username}
    />
  );
}

// Sentinel value for the custom physique_goal pill
const CUSTOM_GOAL_KEY="__physique__";
const STANDARD_GOALS=["Build muscle","Lose fat","Improve strength","General fitness"];

// Compound exercise IDs — used for exercise filtering AND split progression
const COMPOUND_IDS=new Set(["e_bp","e_sq","e_dl","e_opr","e_br","e_pullup","e_dip","e_rdl","e_frsq","e_legpress","e_dbench","e_dbrow","e_lunge","e_incbp","e_chinup","e_tbar","e_gobsq","e_bsq","e_sumo","e_cblrow","e_hksq","e_smsq","e_dbohp","e_arnp","e_pndrow","e_serow"]);

// Exercises banned from Lower/Legs sessions by name fragment
const BANNED_ON_LOWER=["bench press","incline press","decline press","overhead press","shoulder press","dumbbell press","chest press","cable flye","dumbbell flye","pec deck","push up","pushup","dip","row","pull up","pullup","pull-up","chin up","chinup","chin-up","lat pulldown","face pull","shrug","curl","pushdown","tricep extension","skull crusher","incline dumbbell","low cable flye","lateral raise","side raise"];

// Exercises that are squat patterns (max 1 per lower session)
const SQUAT_PATTERNS=["barbell squat","goblet squat","hack squat","box squat","smith machine squat","front squat","overhead squat","zercher squat","safety bar squat"];

// Required isolation across the week
const REQUIRED_WEEKLY={
  lateralRaise:["lateral raise","side raise","side lateral"],
  bicep:["curl"],
  tricep:["pushdown","tricep extension","skull crusher","overhead extension","tricep pushdown"],
};

// Weak finishers that should not appear on upper/push sessions
const WEAK_FINISHERS=["push up","pushup","push-up"];

// Hard-coded exercise IDs from exerciseLibrary.js — used for injection
const INJECT_IDS={
  lateralRaise:"e_n_lateraldb",    // Lateral Raise (Dumbbell)
  bicepCurl:"e_dc",                // Dumbbell Curl
  tricepPushdown:"e_td2",          // Tricep Pushdown (Cable)
  legPress:"e_leg",                // Leg Press
  legCurl:"e_lc",                  // Leg Curl
  legExtension:"e_le",             // Leg Extension
  standingCalfRaise:"e_cr",        // Standing Calf Raise
  bulgarianSplitSquat:"e_bsplit",  // Bulgarian Split Squat
  abWheel:"e_abw",                 // Ab Wheel Rollout
};

// Default lower body exercises for padding stripped lower sessions
const LOWER_BODY_DEFAULTS=[
  {exerciseId:"e_leg",name:"Leg Press",type:"compound",sets:3,reps:"8-10",weight_note:"moderate",rest_seconds:90},
  {exerciseId:"e_lc",name:"Leg Curl",type:"isolation",sets:3,reps:"12-15",weight_note:"moderate",rest_seconds:60},
  {exerciseId:"e_le",name:"Leg Extension",type:"isolation",sets:3,reps:"12-15",weight_note:"moderate",rest_seconds:60},
  {exerciseId:"e_cr",name:"Standing Calf Raise",type:"isolation",sets:3,reps:"15-20",weight_note:"moderate",rest_seconds:60},
  {exerciseId:"e_abw",name:"Ab Wheel Rollout",type:"isolation",sets:3,reps:"10-12",weight_note:"moderate",rest_seconds:60},
];

// Alternate lower body defaults — used for second lower session to avoid identical padding
const LOWER_BODY_DEFAULTS_B=[
  {exerciseId:"e_bsplit",name:"Bulgarian Split Squat",type:"compound",sets:3,reps:"8-10",weight_note:"moderate",rest_seconds:90},
  {exerciseId:"e_lc",name:"Leg Curl",type:"isolation",sets:3,reps:"12-15",weight_note:"moderate",rest_seconds:60},
  {exerciseId:"e_le",name:"Leg Extension",type:"isolation",sets:3,reps:"12-15",weight_note:"moderate",rest_seconds:60},
  {exerciseId:"e_scr",name:"Seated Calf Raise",type:"isolation",sets:3,reps:"15-20",weight_note:"moderate",rest_seconds:60},
  {exerciseId:"e_abw",name:"Ab Wheel Rollout",type:"isolation",sets:3,reps:"10-12",weight_note:"moderate",rest_seconds:60},
];

// Exercises that must never be replaced by Checks 4/5 — primary movements only
const KEEP_PROTECTED=["bench press","incline press","incline barbell","dumbbell bench",
  "overhead press","shoulder press","arnold press","dumbbell press",
  "barbell row","pendlay row","dumbbell row","cable row","t-bar row","seated row",
  "pull up","chin up","lat pulldown","weighted pull","pullup","chinup",
  "deadlift","barbell squat","romanian deadlift","front squat",
  "lateral raise","lateral","side raise"];

// Spinal loading movements — max 2 per lower session
const SPINAL_LOADING_EXERCISES=["squat","deadlift","romanian deadlift","rdl","good morning","barbell row","overhead press","pendlay row","bent over row"];

// Substitution map for Check 7 variety — real IDs from exerciseLibrary.js
const SUBSTITUTION_MAP={
  // Shoulder press alternatives
  "e_dbohp":["e_ohp","e_arnld"],
  "e_ohp":["e_dbohp","e_arnld"],
  "e_arnld":["e_dbohp","e_ohp"],
  // Row alternatives
  "e_br":["e_pbr","e_dbr","e_csr"],
  "e_pbr":["e_br","e_dbr","e_csr"],
  "e_dbr":["e_br","e_pbr","e_csr"],
  "e_csr":["e_br","e_pbr","e_dbr"],
  "e_tbr":["e_br","e_pbr","e_dbr"],
  // Pulldown / pull up alternatives
  "e_lat":["e_pu2","e_wglat","e_cg"],
  "e_pu2":["e_lat","e_wglat","e_cg"],
  "e_wglat":["e_lat","e_pu2","e_cg"],
  "e_cg":["e_lat","e_pu2","e_wglat"],
  "e_wpu2":["e_lat","e_pu2","e_cg"],
  // Bench press alternatives
  "e_bp":["e_dbench","e_idbp","e_ibp"],
  "e_dbench":["e_bp","e_idbp","e_ibp"],
  "e_ibp":["e_bp","e_dbench","e_idbp"],
  "e_idbp":["e_bp","e_dbench","e_ibp"],
};

// ── Client-side programme validator — catches LLM structure violations ──────
function validateAndPatchWeek1(week1Sessions,libMap){
  if(!week1Sessions||!week1Sessions.length) return week1Sessions;
  console.log(`[ProgrammeValidator] Running. Sessions: ${week1Sessions?.length}, Library entries in map: ${libMap?.size ?? "no libMap"}`);

  // Helper: resolve exercise name from exerciseId via libMap
  const nameOf=ex=>{
    const lib=libMap?.get?.(ex.exerciseId);
    return (ex.name||lib?.name||ex.exerciseId||"").toLowerCase();
  };

  // Check 0 — Remove weak finishers (push ups) from upper/push sessions
  week1Sessions.forEach(s=>{
    const label=(s.label||"").toLowerCase();
    const isUpper=label.includes("upper")||label.includes("push");
    if(!isUpper) return;
    s.exercises=s.exercises.filter(ex=>{
      const name=nameOf(ex);
      const weak=WEAK_FINISHERS.some(w=>name.includes(w));
      if(weak) console.warn(`[ProgrammeValidator] Removed weak finisher "${name}" from ${s.label}`);
      return !weak;
    });
  });

  // Check 0b — Remove duplicate exerciseIds within any session
  week1Sessions.forEach(s=>{
    const seen=new Set();
    s.exercises=s.exercises.filter(ex=>{
      if(seen.has(ex.exerciseId)){
        console.warn(`[ProgrammeValidator] Removed duplicate "${nameOf(ex)}" from ${s.label}`);
        return false;
      }
      seen.add(ex.exerciseId);
      return true;
    });
  });

  // Check 1 — Remove banned exercises from lower sessions
  week1Sessions.forEach(s=>{
    const label=(s.label||"").toLowerCase();
    const isLower=label.includes("lower")||label.includes("legs")||label.includes("leg");
    if(!isLower) return;
    s.exercises=s.exercises.filter(ex=>{
      const name=nameOf(ex);
      const banned=BANNED_ON_LOWER.some(b=>name.includes(b));
      if(banned) console.warn(`[ProgrammeValidator] Removed "${name}" from ${s.label} — banned on lower day`);
      return !banned;
    });
  });

  // Check 2 — Enforce max 1 squat pattern per lower session
  week1Sessions.forEach(s=>{
    const label=(s.label||"").toLowerCase();
    const isLower=label.includes("lower")||label.includes("legs")||label.includes("leg");
    if(!isLower) return;
    let squatCount=0;
    s.exercises=s.exercises.filter(ex=>{
      const name=nameOf(ex);
      const isSquat=SQUAT_PATTERNS.some(p=>name.includes(p));
      if(isSquat){
        squatCount++;
        if(squatCount>1){
          console.warn(`[ProgrammeValidator] Removed duplicate squat "${name}" from ${s.label}`);
          return false;
        }
      }
      return true;
    });
  });

  // Check 2b — Pad lower sessions to 5 exercises if stripped below target
  // Alternates between LOWER_BODY_DEFAULTS (A) and LOWER_BODY_DEFAULTS_B (B)
  let lowerSessionIndex=0;
  week1Sessions.forEach(s=>{
    const label=(s.label||"").toLowerCase();
    const isLower=label.includes("lower")||label.includes("legs")||label.includes("leg");
    if(!isLower) return;
    console.log(`[ProgrammeValidator] Check 2b: ${s.label} has ${s.exercises.length} exercises — ${s.exercises.length<5?"PADDING":"ok"}`);
    if(s.exercises.length>=5){ lowerSessionIndex++; return; }
    const defaults=lowerSessionIndex%2===0?LOWER_BODY_DEFAULTS:LOWER_BODY_DEFAULTS_B;
    lowerSessionIndex++;
    const existing=new Set(s.exercises.map(ex=>ex.exerciseId));
    let padIdx=0;
    while(s.exercises.length<5&&padIdx<defaults.length){
      const pad=defaults[padIdx];
      padIdx++;
      console.log(`[ProgrammeValidator] Attempting to pad with ${pad.exerciseId} — already has: ${existing.has(pad.exerciseId)}`);
      if(!libMap?.has?.(pad.exerciseId)){
        console.error(`[ProgrammeValidator] PADDING FAILED — ${pad.exerciseId} not found in library. Library size: ${libMap?.size}`);
        continue;
      }
      if(existing.has(pad.exerciseId)) continue;
      console.log(`[ProgrammeValidator] Padding ${s.label} with ${pad.name} (${s.exercises.length+1}/5)`);
      s.exercises.push({...pad});
      existing.add(pad.exerciseId);
    }
  });

  // Check 2c — Enforce max 2 spinal loading movements per lower session
  const MAX_SPINAL=2;
  week1Sessions.forEach(s=>{
    const label=(s.label||"").toLowerCase();
    const isLower=label.includes("lower")||label.includes("legs")||label.includes("leg");
    if(!isLower) return;
    let spinalCount=0;
    s.exercises=s.exercises.filter(ex=>{
      const name=nameOf(ex);
      const isSpinal=SPINAL_LOADING_EXERCISES.some(sp=>name.includes(sp));
      if(isSpinal){
        spinalCount++;
        if(spinalCount>MAX_SPINAL){
          console.warn(`[ProgrammeValidator] Removed excess spinal load "${name}" from ${s.label} (count was ${spinalCount})`);
          return false;
        }
      }
      return true;
    });
  });

  // Check 2c padding — re-pad if spinal removal dropped below 5
  lowerSessionIndex=0;
  week1Sessions.forEach(s=>{
    const label=(s.label||"").toLowerCase();
    const isLower=label.includes("lower")||label.includes("legs")||label.includes("leg");
    if(!isLower) return;
    if(s.exercises.length>=5){ lowerSessionIndex++; return; }
    const defaults=lowerSessionIndex%2===0?LOWER_BODY_DEFAULTS:LOWER_BODY_DEFAULTS_B;
    lowerSessionIndex++;
    const existing=new Set(s.exercises.map(ex=>ex.exerciseId));
    let padIdx=0;
    while(s.exercises.length<5&&padIdx<defaults.length){
      const pad=defaults[padIdx++];
      if(existing.has(pad.exerciseId)) continue;
      if(!libMap?.has?.(pad.exerciseId)) continue;
      console.log(`[ProgrammeValidator] Check 2c re-padding ${s.label} with ${pad.name} (${s.exercises.length+1}/5)`);
      s.exercises.push({...pad});
      existing.add(pad.exerciseId);
    }
  });

  // Check 3 — Enforce lateral raises on every upper/push session
  week1Sessions.forEach(s=>{
    const label=(s.label||"").toLowerCase();
    const isUpperPush=label.includes("upper")||label.includes("push");
    if(!isUpperPush) return;
    const hasLateral=s.exercises.some(ex=>{
      const name=nameOf(ex);
      return REQUIRED_WEEKLY.lateralRaise.some(r=>name.includes(r));
    });
    if(!hasLateral){
      // If at capacity (6 for upper), remove last non-protected exercise to make room
      if(s.exercises.length>=6){
        for(let i=s.exercises.length-1;i>=0;i--){
          const n=nameOf(s.exercises[i]);
          const isProtected=KEEP_PROTECTED.some(p=>n.includes(p));
          if(!isProtected){
            console.warn(`[ProgrammeValidator] Replacing "${n}" with lateral raise in ${s.label}`);
            s.exercises.splice(i,1);
            break;
          }
        }
      }
      console.warn(`[ProgrammeValidator] Adding lateral raise (${INJECT_IDS.lateralRaise}) to ${s.label}`);
      s.exercises.push({exerciseId:INJECT_IDS.lateralRaise,name:"Lateral Raise",type:"isolation",sets:3,reps:"12-15",weight_note:"moderate",rest_seconds:60});
    }
  });

  // Check 4 — Patch missing bicep isolation
  const allExerciseNames=()=>week1Sessions.flatMap(s=>s.exercises).map(ex=>nameOf(ex));
  const hasBicep=REQUIRED_WEEKLY.bicep.some(b=>allExerciseNames().some(n=>n.includes(b)&&!n.includes("leg curl")));
  if(!hasBicep){
    const targetBicep=week1Sessions.find(s=>{
      const l=(s.label||"").toLowerCase();
      return (l.includes("upper")||l.includes("pull"));
    });
    if(targetBicep){
      // If at capacity, remove last non-protected exercise to make room
      if(targetBicep.exercises.length>=6){
        for(let i=targetBicep.exercises.length-1;i>=0;i--){
          const n=nameOf(targetBicep.exercises[i]);
          const isProtected=KEEP_PROTECTED.some(p=>n.includes(p));
          if(!isProtected){
            console.warn(`[ProgrammeValidator] Replacing "${n}" with bicep curl in ${targetBicep.label}`);
            targetBicep.exercises.splice(i,1);
            break;
          }
        }
      }
      console.warn(`[ProgrammeValidator] Adding bicep curl (${INJECT_IDS.bicepCurl}) to ${targetBicep.label}`);
      targetBicep.exercises.push({exerciseId:INJECT_IDS.bicepCurl,name:"Dumbbell Curl",type:"isolation",sets:3,reps:"12-15",weight_note:"moderate",rest_seconds:60});
    }
  }

  // Check 5 — Patch missing tricep isolation
  const hasTricep=REQUIRED_WEEKLY.tricep.some(t=>allExerciseNames().some(n=>n.includes(t)));
  if(!hasTricep){
    const targetTricep=week1Sessions.find(s=>{
      const l=(s.label||"").toLowerCase();
      return (l.includes("upper")||l.includes("push"));
    });
    if(targetTricep){
      // If at capacity, remove last non-protected exercise to make room
      if(targetTricep.exercises.length>=6){
        for(let i=targetTricep.exercises.length-1;i>=0;i--){
          const n=nameOf(targetTricep.exercises[i]);
          const isProtected=KEEP_PROTECTED.some(p=>n.includes(p))||n.includes("curl");
          if(!isProtected){
            console.warn(`[ProgrammeValidator] Replacing "${n}" with tricep pushdown in ${targetTricep.label}`);
            targetTricep.exercises.splice(i,1);
            break;
          }
        }
      }
      console.warn(`[ProgrammeValidator] Adding tricep pushdown (${INJECT_IDS.tricepPushdown}) to ${targetTricep.label}`);
      targetTricep.exercises.push({exerciseId:INJECT_IDS.tricepPushdown,name:"Tricep Pushdown",type:"isolation",sets:3,reps:"12-15",weight_note:"moderate",rest_seconds:60});
    }
  }

  // Check 6 — Cap sessions by type: upper/push = 6, lower/legs = 5
  week1Sessions.forEach(s=>{
    const label=(s.label||"").toLowerCase();
    const isUpper=label.includes("upper")||label.includes("push");
    const maxEx=isUpper?6:5;
    if(s.exercises.length>maxEx){
      console.warn(`[ProgrammeValidator] Trimming ${s.label} from ${s.exercises.length} to ${maxEx} exercises`);
      s.exercises=s.exercises.slice(0,maxEx);
    }
  });

  // Check 7 — Variety: actively substitute duplicate exercises across same-label session pairs
  const labelGroups={};
  week1Sessions.forEach(s=>{
    const base=(s.label||"").replace(/\s*[AB]\s*$/i,"").trim().toLowerCase();
    if(!labelGroups[base]) labelGroups[base]=[];
    labelGroups[base].push(s);
  });
  for(const group of Object.values(labelGroups)){
    if(group.length<2) continue;
    const firstIds=new Set(group[0].exercises.map(ex=>ex.exerciseId));
    // Collect all IDs across the entire group to avoid substituting to something already present
    const allGroupIds=new Set(group.flatMap(s=>s.exercises.map(ex=>ex.exerciseId)));
    for(let g=1;g<group.length;g++){
      group[g].exercises=group[g].exercises.map(ex=>{
        if(!firstIds.has(ex.exerciseId)) return ex;
        // Try to substitute using SUBSTITUTION_MAP
        const alts=SUBSTITUTION_MAP[ex.exerciseId];
        if(!alts||alts.length===0){
          console.warn(`[ProgrammeValidator] Duplicate "${nameOf(ex)}" in ${group[g].label} — no substitutions available, keeping`);
          return ex;
        }
        // Find first alternative that exists in library and isn't already in this group
        const sub=alts.find(id=>libMap?.has?.(id)&&!allGroupIds.has(id));
        if(!sub){
          console.warn(`[ProgrammeValidator] Duplicate "${nameOf(ex)}" in ${group[g].label} — all substitutions exhausted, keeping`);
          return ex;
        }
        const subLib=libMap.get(sub);
        console.warn(`[ProgrammeValidator] Replacing duplicate "${nameOf(ex)}" with "${subLib?.name||sub}" in ${group[g].label}`);
        allGroupIds.add(sub);
        return {...ex,exerciseId:sub,name:subLib?.name||sub};
      });
    }
  }

  return week1Sessions;
}

// Sanitise user-provided additional instructions (strips dangerous chars, caps length)
function sanitiseProgrammeInstructions(raw){
  if(!raw||typeof raw!=="string") return "";
  return raw.replace(/[{}[\]<>`\\]/g,"").replace(/\n+/g," ").trim().slice(0,300);
}

export default function ProgrammesScreen({programmes,routines,sessions,library,onSaveProgramme,onSaveRoutine,onDeleteProgramme,onSetActive,persona,profile,effectiveIsPremium,userId,darkMode,setDarkMode,onNavigate}){
  const [view,setView]=useState("list");
  const [editProg,setEditProg]=useState(null);
  const [name,setName]=useState("");
  const [numWeeks,setNumWeeks]=useState(4);
  const [weeks,setWeeks]=useState([]);
  const [saving,setSaving]=useState(false);
  const [saveError,setSaveError]=useState("");
  const [aiGenLoading,setAiGenLoading]=useState(false);
  const [aiGenError,setAiGenError]=useState("");
  const [aiMatchHint,setAiMatchHint]=useState("");
  const [showAiSetup,setShowAiSetup]=useState(false);
  const [aiGoal,setAiGoal]=useState("");
  // Pre-select physique_goal pill when profile loads (runs once when data arrives)
  const hasPhysique=!!(profile?.physique_goal||"").trim();
  useEffect(()=>{
    if(!aiGoal&&hasPhysique) setAiGoal(CUSTOM_GOAL_KEY);
  },[hasPhysique]); // eslint-disable-line react-hooks/exhaustive-deps
  const [aiDays,setAiDays]=useState(0);
  const [aiEquipment,setAiEquipment]=useState("");
  const [aiTime,setAiTime]=useState("");
  const [additionalInstructions,setAdditionalInstructions]=useState("");
  const [aiSaveProgress,setAiSaveProgress]=useState("");
  // Review screen state
  const [aiReview,setAiReview]=useState(null); // holds parsed AI result + validated sessions
  const [reviewExpanded,setReviewExpanded]=useState({}); // {weekIdx: true/false}
  const [reviewSaving,setReviewSaving]=useState(false);
  const [reviewError,setReviewError]=useState("");
  // Feedback chat state
  const [feedbackText,setFeedbackText]=useState("");
  const [feedbackLoading,setFeedbackLoading]=useState(false);
  const [feedbackError,setFeedbackError]=useState("");
  const [feedbackOk,setFeedbackOk]=useState("");
  const [feedbackHistory,setFeedbackHistory]=useState([]); // previous refinement requests
  const [dayExpanded,setDayExpanded]=useState({}); // {`${week}-${day}`: true/false}

  function openBuilder(p){
    if(p){
      setName(p.name); setNumWeeks((p.weeks||[]).length||4);
      setWeeks((p.weeks||[]).map(w=>({days:(w.days||[]).map(d=>({...d}))})));
      setEditProg(p);
    } else {
      setName(""); setNumWeeks(4);
      setWeeks(Array.from({length:4},()=>({days:[{label:"Day 1",routineId:""},{label:"Day 2",routineId:""},{label:"Day 3",routineId:""}]})));
      setEditProg(null);
    }
    setView("builder");
  }

  function rebuildWeeks(n){
    setNumWeeks(n);
    setWeeks(prev=>{
      const next=[];
      for(let i=0;i<n;i++) next.push(prev[i]||{days:[{label:"Day 1",routineId:""},{label:"Day 2",routineId:""},{label:"Day 3",routineId:""}]});
      return next;
    });
  }
  function addDay(wi){setWeeks(prev=>{const a=[...prev.map(w=>({...w,days:[...w.days]}))];a[wi].days.push({label:`Day ${a[wi].days.length+1}`,routineId:""});return a;});}
  function removeDay(wi,di){setWeeks(prev=>{const a=[...prev.map(w=>({...w,days:[...w.days]}))];a[wi].days=a[wi].days.filter((_,j)=>j!==di);return a;});}
  function setDayRoutine(wi,di,rid){setWeeks(prev=>{const a=[...prev.map(w=>({...w,days:[...w.days.map(d=>({...d}))]}))];a[wi].days[di].routineId=rid;return a;});}
  function setDayLabel(wi,di,lbl){setWeeks(prev=>{const a=[...prev.map(w=>({...w,days:[...w.days.map(d=>({...d}))]}))];a[wi].days[di].label=lbl;return a;});}

  async function handleSave(){
    if(!name.trim()) return;
    setSaving(true);
    setSaveError("");
    try {
      await onSaveProgramme({id:editProg?.id,name:name.trim(),weeks,active:editProg?.active||false,started_at:editProg?.started_at||null});
      setSaving(false);
      setView("list");
    } catch(e) {
      if(process.env.NODE_ENV!=="production") console.error("[handleSave]",e);
      setSaving(false);
      setSaveError("Failed to save. Please try again.");
    }
  }

  // ── AI programme generator with full exercises ──────────────────────────
  function filterExercises(equipmentChoice){
    const all=library||[];
    let filtered;
    if(equipmentChoice==="Bodyweight only"){
      filtered=all.filter(e=>e.category==="Bodyweight");
    } else if(equipmentChoice==="Dumbbells & bodyweight"){
      filtered=all.filter(e=>["Dumbbell","Bodyweight","Cable","Band"].includes(e.category));
    } else {
      filtered=all.filter(e=>!["Mobility","Cardio","Other"].includes(e.category));
    }
    // Prioritise compound movements
    const compounds=filtered.filter(e=>COMPOUND_IDS.has(e.id));
    const rest=filtered.filter(e=>!COMPOUND_IDS.has(e.id));
    const combined=[...compounds,...rest].slice(0,50);
    return combined.map(e=>({id:e.id,name:e.name,muscles:(e.muscles||[]).join(", ")}));
  }

  // Split progression — compounds and isolations progress differently
  const PROG_COMPOUND=[
    {sets:3,reps:"8-10",weight_note:"moderate",label:"Foundation"},
    {sets:4,reps:"8-10",weight_note:"moderate",label:"Volume"},
    {sets:4,reps:"4-6",weight_note:"heavy",label:"Intensity"},
    {sets:3,reps:"8-10",weight_note:"light",label:"Deload"},
  ];
  const PROG_ISOLATION=[
    {sets:3,reps:"12-15",weight_note:"moderate",label:"Foundation"},
    {sets:3,reps:"12-15",weight_note:"moderate-heavy",label:"Volume"},
    {sets:4,reps:"10-12",weight_note:"moderate-heavy",label:"Intensity"},
    {sets:2,reps:"12-15",weight_note:"light",label:"Deload"},
  ];
  // Display-friendly label for review screen
  const WEEK_LABELS=[
    {label:"Foundation",sub:"C: 3×8-10 / I: 3×12-15, RPE 7"},
    {label:"Volume",sub:"C: 4×8-10 / I: 3×12-15, RPE 7-8"},
    {label:"Intensity",sub:"C: 4×4-6 / I: 4×10-12, RPE 8-9"},
    {label:"Deload",sub:"C: 3×8-10 / I: 2×12-15, RPE 5-6"},
  ];

  async function generateWeeklyProgramme(){
    if(!effectiveIsPremium) return;
    setAiGenLoading(true); setAiGenError(""); setAiSaveProgress("Generating programme...");
    let rawResponse="";
    try {
      const filteredEx=filterExercises(aiEquipment);
      const weeksCount=numWeeks||4;
      // Resolve goal text: custom pill → full physique_goal, standard pill → label + physique context
      const goalText=aiGoal===CUSTOM_GOAL_KEY?profile.physique_goal:aiGoal;
      const physiqueCtx=(aiGoal!==CUSTOM_GOAL_KEY&&profile?.physique_goal)?`\n- User physique goal: ${profile.physique_goal}`:"";
      // Generate ONLY week 1 — client duplicates for remaining weeks with progression
      rawResponse=await llmFetch({
        model:LLM_MODEL_HEAVY, max_tokens:2000, temperature:0.4, reasoning_effort:"none",
        messages:[
          {role:"system",content:`You are an elite strength and physique coach writing programmes for intermediate-advanced lifters. Generate ONE week of training sessions. Use ONLY exercise IDs from the provided list. Return ONLY valid JSON — no markdown, no commentary, no text outside the JSON object. Every rule below is MANDATORY — violating any single rule makes the programme invalid and unusable.

PROGRAMME STRUCTURE LAW:

DAY COUNT: Generate EXACTLY ${aiDays} sessions. Not ${aiDays-1}, not ${aiDays+1}. Exactly ${aiDays}.

SPLIT SELECTION:
- 3 days: Push/Pull/Legs OR Full Body x3
- 4 days: Upper A / Lower A / Upper B / Lower B
- 5 days: Push/Pull/Legs/Upper/Lower
- 6 days: Push/Pull/Legs x2
Never use PPL x2 for 4 days.

EXERCISE PLACEMENT LAW — ABSOLUTE, NO EXCEPTIONS:
Push days MUST contain: horizontal press, overhead press, chest isolation, tricep isolation, lateral raise.
Push days MUST NOT contain: squats, deadlifts, RDLs, leg press, lunges, leg curls, leg extensions, rows, pull ups, lat pulldowns, curls.
Pull days MUST contain: horizontal pull (row), vertical pull (pulldown/pull up), rear delt, bicep isolation.
Pull days MUST NOT contain: bench press, incline press, overhead press, chest flyes, squats, deadlifts, leg press, lunges, leg curls, leg extensions, lateral raises, tricep pushdowns.
Legs/Lower days MUST contain: squat pattern, hip hinge, quad isolation, hamstring isolation, optional calves.
Legs/Lower days MUST NOT contain: bench press, incline press, overhead press, dumbbell press, chest press, chest flyes, rows, pull ups, lat pulldowns, push ups, dips, shrugs, curls, tricep pushdowns, tricep extensions, lateral raises. If you are about to place ANY of these on a Legs or Lower day, STOP and substitute a lower body exercise.
Upper days MUST contain: 1 horizontal press + 1 vertical press + 1 horizontal pull + 1 vertical pull + lateral raise + 1-2 arm isolations.
Upper days MUST NOT contain: squats, deadlifts, leg press, lunges, leg curls, leg extensions, hip thrusts.

UPPER SESSION BALANCE LAW:
Every Upper session must include BOTH pressing and pulling in roughly equal volume. Minimum: 2 press movements (1 horizontal + 1 vertical) AND 2 pull movements (1 horizontal + 1 vertical). An Upper session with 3 presses and 1 pull, or vice versa, is imbalanced and invalid.

SESSION TIME LAW:
Lower and Legs sessions: exactly 5 exercises.
Upper and Push sessions: exactly 6 exercises.
Upper session structure (6 exercises):
1. One horizontal push (bench or incline press)
2. One overhead press or vertical push
3. One horizontal pull (row)
4. One vertical pull (pulldown or pull up)
5. Lateral raises — mandatory
6. One arm isolation (curl OR pushdown)
Lower session structure (5 exercises):
1. One primary squat pattern
2. One hip hinge (Romanian deadlift OR deadlift — not both)
3. One unilateral or machine quad movement
4. One hamstring isolation (leg curl)
5. One calf or core movement
Do not generate upper sessions with fewer than 6 exercises.
Do not generate lower sessions with fewer than 5 exercises.

SESSION ORDER: Compounds FIRST, isolation LAST. Never programme a flye before a press or a curl before a row.

MANDATORY CONTENT:
- Every push session AND every upper session: at least 1 overhead press movement.
- Every pull session AND every upper session: at least 1 vertical pull AND 1 horizontal pull.
- Every legs/lower session: at least 1 quad-dominant compound AND 1 hamstring-dominant compound.
- Direct bicep isolation in at least 2 separate sessions per week.
- Direct tricep isolation in at least 2 separate sessions per week.

ISOLATION MANDATORY LAW — NON-NEGOTIABLE:
1. Lateral raises MUST appear in EVERY push session and EVERY upper session. A push or upper session without lateral raises is INVALID — reject it and add them.
2. Direct bicep isolation (barbell curl, dumbbell curl, cable curl, hammer curl, incline curl, preacher curl) MUST appear in at least 2 sessions per week. Rows and pull ups do NOT count as bicep isolation.
3. Direct tricep isolation (pushdown, overhead tricep extension, skull crusher, close grip bench) MUST appear in at least 2 sessions per week. Bench press and OHP do NOT count as tricep isolation.
4. Lateral raises are slot 5 on every upper session. Arm isolation is slot 6 on every upper session.
5. Alternate arm isolation across the two upper sessions: Upper A slot 6 = bicep isolation (curl). Upper B slot 6 = tricep isolation (pushdown or extension). Every upper session must have exactly these 6 slots filled.

PROHIBITED:
- Max 2 hip hinge movements per session.
- No duplicate movement patterns in same session (DB Flye + Cable Flye = banned; Lat Pulldown + Pull Up = banned).
- Deadlift and Squat NEVER in the same session.
- No exercise appearing more than 2x per week total.

LOWER DAY SPINAL LOADING:
Every Lower or Legs session may contain a maximum of 2 spinal loading movements. Spinal loading movements are: squat, deadlift, Romanian deadlift, good morning, barbell row. Combining squat + deadlift + Romanian deadlift in one session is prohibited — choose squat + Romanian deadlift OR deadlift + Romanian deadlift, never all three.

QUAD COMPOUND LAW:
Every Lower or Legs session may contain MAXIMUM ONE primary bilateral squat pattern (barbell squat, goblet squat, hack squat, Smith machine squat). If a second lower body compound is needed, it MUST be a completely different movement pattern: hip hinge (RDL, deadlift), unilateral leg (Bulgarian split squat, lunge, step up), or machine (leg press). Two barbell squat variations in one session = INVALID. Two machine squat variations in one session = INVALID. Squat + front squat in one session = INVALID.

VARIETY: If two sessions share a label (Upper A/Upper B, Lower A/Lower B, Push A/Push B), they MUST use mostly different exercises.

VARIETY ENFORCEMENT:
Before finalising, compare every pair of same-label sessions. If any exercise appears in BOTH with the same ID, replace it in the second session with a different exercise for the same muscle. Examples: DB Shoulder Press (A) → Barbell OHP (B). Lat Pulldown (A) → Pull Up (B). Barbell Curl (A) → Cable Curl (B).

SHOULDER PRESS VARIETY: If Dumbbell Shoulder Press appears in Upper A, Upper B must use Barbell Overhead Press or Arnold Press instead. Never use the same shoulder press variation in two sessions in the same week.

MOBILITY DAY LAW:
If a session is labelled Mobility, Flexibility, or Active Recovery, it must contain ONLY recovery work: dynamic stretching, foam rolling, yoga movements, band pull-aparts, face pulls, external rotations, dead bugs, bird dogs, pallof press, carries, loaded stretches. Mobility days MUST NOT contain any barbell compound, heavy dumbbell press, pull ups as primary exercise, or any movement programmed for strength stimulus.

FINAL RULE — CHECK BEFORE YOU OUTPUT:
Count the sessions. If the count does not match the user's requested days exactly, fix it.
Look at every upper and push session. If lateral raises are not in the exercise list, add them now.
Look at the full week. If no session contains a curl, add a curl to one upper or pull session.
Look at the full week. If no session contains a pushdown or tricep extension, add one to an upper or push session.
Look at every lower and legs session. If any exercise is a press, flye, row, pull up, pulldown, curl, or pushdown, remove it and replace with a lower body exercise.
Do this check silently. Then output the JSON.
${additionalInstructions.trim()?`\nADDITIONAL USER INSTRUCTIONS (apply if they do not contradict any law above):\n${sanitiseProgrammeInstructions(additionalInstructions)}\n`:""}
${AI_SAFETY_LAW} ${WORKOUT_SAFETY_LAW} ${INJURY_LAW}`},
          {role:"user",content:`Generate WEEK 1 ONLY of a ${weeksCount}-week programme. I will duplicate and apply progression myself.

USER PROFILE:
- Goal: ${goalText}${physiqueCtx}
- Days per week: ${aiDays}
- Equipment: ${aiEquipment}
- Time per session: ${aiTime}
- Weight: ${profile?.weight||"unknown"}kg

EXERCISE COUNT BY TIME:
- 30 mins: 3-4 exercises
- 45 mins: 4-5 exercises
- 60 mins: 5-6 exercises
- 90 mins: 6-8 exercises

WEEK 1 SCHEME (Foundation):
- Compounds: 3 sets x 8-10 reps, moderate weight, RPE 7
- Isolation: 3 sets x 12-15 reps, moderate weight, RPE 6-7

Mark each exercise as compound or isolation using a "type" field ("compound" or "isolation").

Include a "summary" field: 3-4 sentences, plain English, second person, no jargon.
Include a "week_structure" field: weekly day pattern e.g. "Upper A · Lower A · Rest · Upper B · Lower B · Rest · Rest"

AVAILABLE EXERCISES (use ONLY these IDs):
${JSON.stringify(filteredEx)}

Return JSON:
{"programme_name":"string","summary":"string","week_structure":"string","sessions":[{"day":1,"label":"Upper A","exercises":[{"exerciseId":"string","type":"compound","sets":3,"reps":"8-10","weight_note":"moderate","rest_seconds":90}]}]}`}
        ]
      });

      // Parse with detailed error handling
      let cleaned;
      try {
        cleaned=cleanAIResponse(rawResponse);
        if(!cleaned){
          console.error("[AI Programme] cleanAIResponse returned empty. Raw length:",rawResponse?.length,"Raw start:",rawResponse?.substring?.(0,200));
          throw new Error("AI returned empty response");
        }
      } catch(parseErr){
        console.error("[AI Programme] cleanAIResponse failed:",parseErr?.message,"Raw length:",rawResponse?.length,"Raw start:",rawResponse?.substring?.(0,300));
        throw new Error("AI response could not be parsed — the model may have returned incomplete output");
      }

      let obj;
      try {
        obj=JSON.parse(cleaned);
      } catch(jsonErr){
        console.error("[AI Programme] JSON.parse failed:",jsonErr?.message,"Cleaned length:",cleaned?.length,"Cleaned start:",cleaned?.substring?.(0,300),"Cleaned end:",cleaned?.substring?.(cleaned.length-200));
        throw new Error("AI returned malformed JSON — try again");
      }

      if(!obj.sessions||!Array.isArray(obj.sessions)||obj.sessions.length===0){
        console.error("[AI Programme] Invalid structure — sessions missing or empty. Keys:",Object.keys(obj));
        throw new Error("AI returned invalid programme structure — no sessions found");
      }

      // Validate exercise IDs against library
      const libIds=new Set((library||[]).map(e=>e.id));
      const libMap=new Map((library||[]).map(e=>[e.id,e]));
      let week1Sessions=obj.sessions.map(s=>({
        ...s,
        exercises:(s.exercises||[]).filter(ex=>{
          if(!libIds.has(ex.exerciseId)){console.error("[AI Programme] Unknown exercise ID:",ex.exerciseId);return false;}
          return true;
        })
      })).filter(s=>s.exercises.length>0);

      if(week1Sessions.length===0){
        console.error("[AI Programme] All exercises filtered out — no valid IDs matched.");
        throw new Error("None of the AI-selected exercises matched the library — try again");
      }

      // Client-side validation — catches LLM structure violations
      week1Sessions=validateAndPatchWeek1(week1Sessions,libMap);

      // Duplicate week 1 across all weeks with split compound/isolation progression
      const allSessions=[];
      for(let w=1;w<=weeksCount;w++){
        const cycleIdx=(w-1)%4;
        const progC=PROG_COMPOUND[cycleIdx];
        const progI=PROG_ISOLATION[cycleIdx];
        for(const s of week1Sessions){
          allSessions.push({
            ...s,
            week:w,
            exercises:s.exercises.map(ex=>{
              // Determine if compound: use AI-provided type field, fallback to COMPOUND_IDS lookup
              const isCompound=ex.type==="compound"||COMPOUND_IDS.has(ex.exerciseId);
              const prog=isCompound?progC:progI;
              return {...ex,sets:prog.sets,reps:prog.reps,weight_note:prog.weight_note};
            }),
          });
        }
      }

      // Show review screen
      setAiReview({
        programmeName:obj.programme_name||"AI Programme",
        summary:obj.summary||"",
        weekStructure:obj.week_structure||"",
        validSessions:allSessions,
        weeksCount,
        libMap,
      });
      setReviewExpanded({});
      setReviewError("");
      setShowAiSetup(false);
    } catch(e){
      console.error("[generateWeeklyProgramme] error:",e);
      if(rawResponse) console.error("[generateWeeklyProgramme] raw response length:",rawResponse?.length);
      const msg=e?.code==="TIMEOUT"
        ?"This is taking longer than expected — please try again."
        :e?.message||"Could not generate programme. Please try again.";
      setAiGenError(msg);
    }
    setAiGenLoading(false); setAiSaveProgress("");
  }

  // Save the reviewed programme to Supabase
  async function saveReviewedProgramme(){
    if(!aiReview) return;
    setReviewSaving(true); setReviewError(""); setAiSaveProgress("");
    try {
      const {validSessions,weeksCount,programmeName}=aiReview;
      const createdRoutines=[];
      for(let i=0;i<validSessions.length;i++){
        const s=validSessions[i];
        setAiSaveProgress(`Saving workouts... (${i+1}/${validSessions.length})`);
        const routineName=`${s.label||"Day "+s.day} — Wk ${s.week}`;
        const routineExercises=s.exercises.map(ex=>({
          exerciseId:ex.exerciseId,
          sets:ex.sets||3,
          reps:ex.reps||"10-12",
          weight_note:ex.weight_note||"moderate",
          rest_seconds:ex.rest_seconds||90
        }));
        const saved=await onSaveRoutine({name:routineName,exercises:routineExercises});
        createdRoutines.push({week:s.week,day:s.day,label:s.label||"Day "+s.day,routineId:saved?.id||"",routineName});
      }
      const progWeeks=[];
      for(let w=1;w<=weeksCount;w++){
        const weekRoutines=createdRoutines.filter(r=>r.week===w).sort((a,b)=>a.day-b.day);
        progWeeks.push({days:weekRoutines.map(r=>({label:r.label,routineId:r.routineId}))});
      }
      setAiSaveProgress("");
      setName(programmeName);
      setNumWeeks(progWeeks.length);
      setWeeks(progWeeks);
      setEditProg(null);
      setAiMatchHint("");
      setAiReview(null);
      setView("builder");
    } catch(e){
      console.error("[saveReviewedProgramme] error:",e);
      setReviewError("Could not save programme. Please try again.");
    }
    setReviewSaving(false); setAiSaveProgress("");
  }

  // ── Refine programme via feedback chat ─────────────────────────────────
  async function refineProgramme(){
    if(!aiReview||!feedbackText.trim()) return;
    setFeedbackLoading(true); setFeedbackError(""); setFeedbackOk("");
    try {
      const filteredEx=filterExercises(aiEquipment);
      const goalText=aiGoal===CUSTOM_GOAL_KEY?profile.physique_goal:aiGoal;
      // Send only week 1 sessions for refinement (client re-duplicates after)
      const week1Sessions=aiReview.validSessions.filter(s=>s.week===1);
      const raw=await llmFetch({
        model:LLM_MODEL_HEAVY, max_tokens:2000, temperature:0.4, reasoning_effort:"none",
        messages:[
          {role:"system",content:`You are an expert strength coach. The user reviewed their programme and wants changes. Make ONLY the changes requested. Keep all other sessions unchanged. Use ONLY exercise IDs from the provided list. Return ONLY valid JSON — no markdown. Return week 1 only.

All PROGRAMME STRUCTURE LAW rules still apply: correct exercise placement per day type (upper body exercises NEVER on Legs/Lower days), compounds first, no prohibited combinations, mandatory content per session type, lateral raises mandatory on every Push/Upper session, bicep and tricep isolation each in at least 2 sessions/week. Mark each exercise "type":"compound" or "type":"isolation".

SELF-CHECK before returning: verify no upper body exercises on Legs/Lower days, lateral raises present on every Push/Upper session, bicep isolation in >=2 sessions, tricep isolation in >=2 sessions, Upper sessions have >=2 press and >=2 pull movements.
${additionalInstructions.trim()?`\nADDITIONAL USER INSTRUCTIONS (apply if they do not contradict any law above):\n${sanitiseProgrammeInstructions(additionalInstructions)}\n`:""}
${AI_SAFETY_LAW} ${WORKOUT_SAFETY_LAW} ${INJURY_LAW}`},
          {role:"user",content:`CURRENT PROGRAMME (Week 1):
${JSON.stringify({programme_name:aiReview.programmeName,summary:aiReview.summary,week_structure:aiReview.weekStructure,sessions:week1Sessions})}

USER REQUEST:
${feedbackText.trim()}

ORIGINAL PARAMETERS:
- Goal: ${goalText}
- Equipment: ${aiEquipment}
- Time available: ${aiTime}
- Days per week: ${aiDays}

AVAILABLE EXERCISES (use ONLY these IDs):
${JSON.stringify(filteredEx)}

Return JSON:
{"programme_name":"string","summary":"string","week_structure":"string","sessions":[{"day":1,"label":"Upper A","exercises":[{"exerciseId":"string","type":"compound","sets":3,"reps":"8-10","weight_note":"moderate","rest_seconds":90}]}]}`}
        ]
      });

      const cleaned=cleanAIResponse(raw);
      if(!cleaned) throw new Error("AI returned empty response");
      const obj=JSON.parse(cleaned);
      if(!obj.sessions||!Array.isArray(obj.sessions)||obj.sessions.length===0){
        throw new Error("AI returned invalid structure");
      }

      // Validate exercise IDs
      const libIds=new Set((library||[]).map(e=>e.id));
      const libMapR=new Map((library||[]).map(e=>[e.id,e]));
      let updatedWeek1=obj.sessions.map(s=>({
        ...s,
        exercises:(s.exercises||[]).filter(ex=>libIds.has(ex.exerciseId))
      })).filter(s=>s.exercises.length>0);

      if(updatedWeek1.length===0) throw new Error("No valid exercises in refined programme");

      // Client-side validation — catches LLM structure violations
      updatedWeek1=validateAndPatchWeek1(updatedWeek1,libMapR);

      // Re-duplicate across all weeks with split compound/isolation progression
      const weeksCount=aiReview.weeksCount;
      const allSessions=[];
      for(let w=1;w<=weeksCount;w++){
        const cycleIdx=(w-1)%4;
        const progC=PROG_COMPOUND[cycleIdx];
        const progI=PROG_ISOLATION[cycleIdx];
        for(const s of updatedWeek1){
          allSessions.push({
            ...s,week:w,
            exercises:s.exercises.map(ex=>{
              const isCompound=ex.type==="compound"||COMPOUND_IDS.has(ex.exerciseId);
              const prog=isCompound?progC:progI;
              return {...ex,sets:prog.sets,reps:prog.reps,weight_note:prog.weight_note};
            }),
          });
        }
      }

      setAiReview(prev=>({
        ...prev,
        programmeName:obj.programme_name||prev.programmeName,
        summary:obj.summary||prev.summary,
        weekStructure:obj.week_structure||prev.weekStructure,
        validSessions:allSessions,
      }));
      setFeedbackHistory(prev=>[...prev,feedbackText.trim()]);
      setFeedbackText("");
      setFeedbackOk("Updated!");
      setTimeout(()=>setFeedbackOk(""),2500);
    } catch(e){
      console.error("[refineProgramme] error:",e);
      setFeedbackError("Could not apply changes — please try again");
    }
    setFeedbackLoading(false);
  }

  // ── AI Review Screen ───────────────────────────────────────────────────
  if(aiReview){
    const {programmeName,summary,weekStructure,validSessions,weeksCount,libMap}=aiReview;
    // Group sessions by week
    const byWeek={};
    for(const s of validSessions){
      if(!byWeek[s.week]) byWeek[s.week]=[];
      byWeek[s.week].push(s);
    }
    // Progression labels
    const progLabels=WEEK_LABELS.map((wl,i)=>({wk:i+1,...wl}));
    const summaryText=summary||`${weeksCount}-week programme targeting ${aiGoal===CUSTOM_GOAL_KEY?profile?.physique_goal||"your goal":aiGoal} with ${aiEquipment}.`;
    const dismissReview=()=>{setAiReview(null);setShowAiSetup(true);setFeedbackText("");setFeedbackHistory([]);setFeedbackError("");setFeedbackOk("");setDayExpanded({});};
    return(
      <div style={{background:T.pageBg}}>
        <PlansHeader title="Review" subtitle="Review & refine" onBack={dismissReview} darkMode={darkMode} setDarkMode={setDarkMode} profile={profile}/>
        <div style={{padding:"14px 16px 24px"}}>
          {/* Programme name + subtitle */}
          <p style={{margin:"0 0 4px",fontSize:24,fontFamily:T.fontDisplay,fontWeight:800,color:T.inkDark,lineHeight:1.1}}>{programmeName}</p>
          <p style={{margin:"0 0 14px",fontSize:12,fontFamily:T.fontSans,color:T.inkMuted,fontWeight:500}}>
            {weeksCount} weeks · {aiDays} days/week · {aiTime}
          </p>

          {/* Summary card */}
          <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,padding:16,marginBottom:14}}>
            <p style={{margin:"0 0 8px",fontSize:10,fontWeight:700,color:T.inkMuted,textTransform:"uppercase",letterSpacing:"1px",fontFamily:T.fontSans}}>Programme summary</p>
            <p style={{margin:0,fontSize:12,fontFamily:T.fontSans,color:T.inkDark,lineHeight:1.6,fontWeight:500}}>{summaryText}</p>
          </div>

          {/* Week structure pills */}
          {weekStructure&&(
            <div style={{marginBottom:14}}>
              <p style={{margin:"0 0 8px",fontSize:10,fontWeight:700,color:T.inkMuted,textTransform:"uppercase",letterSpacing:"1px",fontFamily:T.fontSans}}>Weekly pattern</p>
              <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                {weekStructure.split(/[·•,/]/).map((d,i)=>{
                  const day=d.trim();
                  if(!day) return null;
                  const lc=getLabelColour(day);
                  return <span key={i} style={{background:lc.bg,color:lc.text,padding:"5px 12px",fontSize:10,fontFamily:T.fontSans,fontWeight:700,borderRadius:20,whiteSpace:"nowrap"}}>{day}</span>;
                })}
              </div>
            </div>
          )}

          {/* Progression badges */}
          <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,padding:16,marginBottom:14}}>
            <p style={{margin:"0 0 10px",fontSize:10,fontWeight:700,color:T.inkMuted,textTransform:"uppercase",letterSpacing:"1px",fontFamily:T.fontSans}}>Progression scheme</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
              {progLabels.map(p=>(
                <div key={p.wk} style={{background:T.surfaceLight,borderRadius:12,padding:12}}>
                  <p style={{margin:0,fontSize:14,fontFamily:T.fontDisplay,fontWeight:700,color:T.inkDark}}>Wk {p.wk}: {p.label}</p>
                  <p style={{margin:"4px 0 0",fontSize:11,fontFamily:T.fontSans,color:T.inkMuted,fontWeight:500}}>{p.sub}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Programme preview — collapsible weeks with day-level expand */}
          <p style={{margin:"0 0 10px",fontSize:10,fontWeight:700,color:T.inkMuted,textTransform:"uppercase",letterSpacing:"1px",fontFamily:T.fontSans}}>Programme preview</p>
          {Array.from({length:weeksCount},(_,wi)=>wi+1).map(w=>{
            const weekSessions=(byWeek[w]||[]).sort((a,b)=>a.day-b.day);
            const expanded=!!reviewExpanded[w];
            const wl=WEEK_LABELS[(w-1)%4];
            return(
              <div key={w} style={{marginBottom:8,overflow:"hidden"}}>
                <button onClick={()=>setReviewExpanded(prev=>({...prev,[w]:!prev[w]}))}
                  style={{width:"100%",background:T.tileBg,borderRadius:12,padding:"11px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",border:"none"}}>
                  <span style={{fontSize:14,fontFamily:T.fontDisplay,fontWeight:700,color:"#fff"}}>
                    Week {w} <span style={{fontWeight:500,fontSize:11,color:"rgba(255,255,255,0.5)"}}>— {wl.label}</span>
                  </span>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:10,fontFamily:T.fontSans,color:"rgba(255,255,255,0.5)",fontWeight:600}}>{weekSessions.length} sessions</span>
                    {expanded?<CaretDown size={14} weight="bold" color="rgba(255,255,255,0.5)"/>:<CaretRight size={14} weight="bold" color="rgba(255,255,255,0.5)"/>}
                  </div>
                </button>
                {expanded&&(
                  <div style={{padding:"8px 0 0"}}>
                    {weekSessions.map((s,si)=>{
                      const dayKey=`${w}-${s.day}`;
                      const dayOpen=!!dayExpanded[dayKey];
                      const lc=getLabelColour(s.label);
                      return(
                        <div key={si} style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm,padding:"10px 14px",marginBottom:si<weekSessions.length-1?6:0}}>
                          <button onClick={()=>setDayExpanded(prev=>({...prev,[dayKey]:!prev[dayKey]}))}
                            style={{width:"100%",background:"none",border:"none",padding:0,display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                            <span style={{background:lc.bg,color:lc.text,padding:"4px 10px",fontSize:10,fontFamily:T.fontSans,fontWeight:700,borderRadius:20}}>{s.label||"Day "+s.day}</span>
                            <span style={{fontSize:10,fontFamily:T.fontSans,color:T.inkMuted,fontWeight:500}}>{s.exercises.length} exercises</span>
                            <span style={{marginLeft:"auto"}}>{dayOpen?<CaretDown size={12} weight="bold" color={T.inkMuted}/>:<CaretRight size={12} weight="bold" color={T.inkMuted}/>}</span>
                          </button>
                          {dayOpen&&s.exercises.map((ex,ei)=>{
                            const lib=libMap.get(ex.exerciseId);
                            return(
                              <div key={ei} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0 5px 18px"}}>
                                <span style={{fontSize:11,fontFamily:T.fontSans,color:T.inkDark,fontWeight:500}}>· {ex.name||lib?.name||ex.exerciseId||"Unknown exercise"}</span>
                                <span style={{fontSize:10,fontFamily:T.fontSans,color:T.inkMuted,fontWeight:500,whiteSpace:"nowrap"}}>{ex.sets}×{ex.reps}, {ex.weight_note}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Feedback chat */}
          <div style={{marginTop:16,marginBottom:12}}>
            <p style={{margin:"0 0 8px",fontSize:10,fontWeight:700,color:T.inkMuted,textTransform:"uppercase",letterSpacing:"1px",fontFamily:T.fontSans}}>Want to make changes?</p>
            {feedbackHistory.length>0&&(
              <div style={{marginBottom:8}}>
                {feedbackHistory.map((fb,i)=>(
                  <p key={i} style={{margin:"0 0 4px",fontSize:11,fontFamily:T.fontSans,color:T.inkMuted,fontStyle:"italic",padding:"10px 14px",background:T.surfaceLight,borderRadius:12}}>"{fb}"</p>
                ))}
              </div>
            )}
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <input value={feedbackText} onChange={e=>setFeedbackText(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter"&&feedbackText.trim()&&!feedbackLoading) refineProgramme();}}
                placeholder="e.g. Replace leg day with mobility and athletic performance"
                style={{flex:1,background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:12,color:T.inkDark,fontFamily:T.fontSans,fontSize:12,padding:"10px 14px",outline:"none"}}/>
              <button onClick={refineProgramme} disabled={feedbackLoading||!feedbackText.trim()}
                style={{background:T.primary,border:"none",borderRadius:12,padding:"10px 16px",cursor:feedbackLoading||!feedbackText.trim()?"default":"pointer",opacity:feedbackLoading||!feedbackText.trim()?0.5:1,display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
                {feedbackLoading?<Spinner size={12}/>:<PaperPlaneTilt size={14} weight="bold" color="#fff"/>}
                <span style={{fontSize:12,fontWeight:700,color:"#fff",fontFamily:T.fontSans}}>Refine</span>
              </button>
            </div>
            {feedbackError&&<p style={{margin:"6px 0 0",fontSize:11,fontFamily:T.fontSans,color:T.red,fontWeight:700}}>{feedbackError}</p>}
            {feedbackOk&&<p style={{margin:"6px 0 0",fontSize:11,fontFamily:T.fontSans,color:T.primary,fontWeight:700}}>{feedbackOk}</p>}
          </div>

          {/* Action buttons */}
          {reviewError&&<p style={{color:T.red,fontSize:11,fontFamily:T.fontSans,fontWeight:700,marginBottom:8,padding:"10px 14px",background:T.surfaceLight,border:`1.5px solid ${T.red}`,borderRadius:T.radiusSm}}>{reviewError}</p>}
          {aiSaveProgress&&<p style={{margin:"0 0 8px",fontSize:11,fontFamily:T.fontSans,color:T.primary,fontWeight:700}}>{aiSaveProgress}</p>}
          <button onClick={saveReviewedProgramme} disabled={reviewSaving||feedbackLoading}
            style={{width:"100%",background:T.primary,border:"none",borderRadius:T.radiusSm,padding:14,cursor:reviewSaving?"default":"pointer",opacity:reviewSaving?0.6:1,marginBottom:8}}>
            <span style={{fontSize:15,fontWeight:800,color:"#fff",fontFamily:T.fontDisplay,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
              {reviewSaving?<><Spinner size={14}/> Saving...</>:<><Check size={16} weight="bold"/> Save programme</>}
            </span>
          </button>
          <button onClick={dismissReview} disabled={reviewSaving||feedbackLoading}
            style={{width:"100%",background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm,padding:14,cursor:"pointer"}}>
            <span style={{fontSize:13,fontWeight:600,color:T.inkMuted,fontFamily:T.fontSans}}>Try again</span>
          </button>
        </div>
      </div>
    );
  }

  if(view==="builder"){
    return(
      <div style={{background:T.pageBg}}>
        <PlansHeader title={editProg?"Edit plan":"New plan"} subtitle={editProg?"Edit plan":"New plan"} onBack={()=>setView("list")} darkMode={darkMode} setDarkMode={setDarkMode} profile={profile}
          rightContent={
            <button onClick={handleSave} disabled={saving||!name.trim()}
              style={{background:T.primary,border:"none",borderRadius:12,padding:"8px 16px",cursor:saving||!name.trim()?"default":"pointer",opacity:saving||!name.trim()?0.5:1,display:"flex",alignItems:"center",gap:5}}>
              {saving?<Spinner size={12}/>:<FloppyDisk size={14} weight="bold" color="#fff"/>}
              <span style={{fontSize:12,fontWeight:700,color:"#fff",fontFamily:T.fontSans}}>Save</span>
            </button>
          }
        />
        <div style={{padding:"14px 16px 24px"}}>
          {saveError&&<p style={{color:T.red,fontSize:11,fontFamily:T.fontSans,fontWeight:700,marginBottom:10,padding:"10px 14px",background:T.surfaceLight,border:`1.5px solid ${T.red}`,borderRadius:T.radiusSm}}>{saveError}</p>}
          {aiMatchHint&&<p style={{color:T.inkMuted,fontSize:11,fontFamily:T.fontSans,marginBottom:10,padding:"10px 14px",background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm}}>{aiMatchHint}</p>}
          <Field label="Programme Name" value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. 12-Week Strength Block"/>
          <div style={{marginBottom:14}}>
            <label style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontSans,letterSpacing:"1px",display:"block",marginBottom:6,textTransform:"uppercase",fontWeight:700}}>Number of weeks</label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {[2,4,6,8,10,12,16].map(n=>(
                <button key={n} onClick={()=>rebuildWeeks(n)} style={{background:numWeeks===n?T.primary:"transparent",color:numWeeks===n?"#fff":T.inkMuted,border:numWeeks===n?"none":`1px solid ${T.cardBorder}`,borderRadius:20,padding:"6px 14px",fontSize:12,fontFamily:T.fontSans,fontWeight:600,cursor:"pointer"}}>{n}</button>
              ))}
            </div>
          </div>

          {weeks.map((w,wi)=>(
            <div key={wi} style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,marginBottom:10,overflow:"hidden"}}>
              <div style={{background:T.tileBg,padding:"11px 16px"}}>
                <span style={{fontSize:16,fontFamily:T.fontDisplay,fontWeight:800,color:"#fff"}}>Week {wi+1}</span>
              </div>
              <div style={{padding:"10px 14px 12px"}}>
                {w.days.map((d,di)=>(
                  <div key={di} style={{display:"flex",gap:8,alignItems:"center",marginBottom:6}}>
                    <input value={d.label} onChange={e=>setDayLabel(wi,di,e.target.value)}
                      style={{background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm,color:T.inkDark,fontFamily:T.fontSans,fontWeight:600,fontSize:12,padding:"8px 12px",outline:"none",width:120,boxSizing:"border-box"}}/>
                    <select value={d.routineId} onChange={e=>setDayRoutine(wi,di,e.target.value)}
                      style={{background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm,color:T.inkDark,fontFamily:T.fontSans,fontWeight:500,fontSize:12,padding:"8px 12px",flex:1}}>
                      <option value="">-- Rest day --</option>
                      {routines.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                    <button onClick={()=>removeDay(wi,di)} style={{background:"none",border:"none",cursor:"pointer",color:T.inkFaint||T.inkMuted,display:"flex",justifyContent:"center",flexShrink:0}}><X size={14} weight="bold"/></button>
                  </div>
                ))}
                <button onClick={()=>addDay(wi)} style={{marginTop:4,background:"transparent",border:`1.5px dashed ${T.cardBorder}`,color:T.inkMuted,padding:"10px 0",width:"100%",cursor:"pointer",fontSize:12,fontFamily:T.fontSans,fontWeight:600,borderRadius:T.radiusSm}}>
                  + Add day
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const activeProg = programmes.find(p => p.active);
  const inactiveProgs = programmes.filter(p => !p.active);
  const listSubtitle = activeProg
    ? "1 active programme"
    : programmes.length > 0
      ? `${programmes.length} programme${programmes.length !== 1 ? "s" : ""}`
      : "No plans";

  return(
    <div style={{background:T.pageBg}}>
      <PlansHeader title="Plans" subtitle={listSubtitle} darkMode={darkMode} setDarkMode={setDarkMode} profile={profile}/>
      <div style={{padding:"14px 16px 24px"}}>
        {/* Action buttons */}
        <div style={{display:"flex",gap:8,marginBottom:14}}>
          <button onClick={()=>openBuilder(null)} style={{flex:1,background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm,padding:13,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            <Plus size={14} weight="bold" color={T.primary}/>
            <span style={{fontSize:13,fontWeight:700,color:T.primary,fontFamily:T.fontSans}}>New plan</span>
          </button>
          <button onClick={()=>{setShowAiSetup(true);setAiGenError("");}} disabled={aiGenLoading}
            style={{flex:1,background:T.tileBg,border:"none",borderRadius:T.radiusSm,padding:13,cursor:aiGenLoading?"default":"pointer",opacity:aiGenLoading?0.6:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            <div style={{width:6,height:6,borderRadius:"50%",background:T.orange}}/>
            <span style={{fontSize:13,fontWeight:700,color:"#fff",fontFamily:T.fontSans}}>AI Generate</span>
          </button>
        </div>

        {aiGenError&&<p style={{color:T.red,fontSize:11,fontFamily:T.fontSans,fontWeight:700,marginBottom:10,padding:"10px 14px",background:T.surfaceLight,border:`1.5px solid ${T.red}`,borderRadius:T.radiusSm}}>{aiGenError}</p>}

        {/* Empty state */}
        {programmes.length===0&&(
          <div style={{background:T.card,borderRadius:18,border:`1px solid ${T.cardBorder}`,padding:"32px 16px",textAlign:"center",margin:"0 0 14px"}}>
            <CalendarBlank size={48} color={T.inkFaint||T.inkMuted} style={{margin:"0 auto 12px",display:"block"}}/>
            <p style={{fontSize:22,fontFamily:T.fontDisplay,fontWeight:800,color:T.inkDark,margin:"0 0 6px"}}>No plans yet</p>
            <p style={{fontSize:12,fontWeight:500,color:T.inkMuted,lineHeight:1.5,margin:0,fontFamily:T.fontSans}}>Generate a training plan with AI or build one manually</p>
          </div>
        )}

        {/* Active programme */}
        {activeProg&&(()=>{
          const p=activeProg;
          const {week,day}=getActiveProgrammeProgress(p);
          const totalDays=(p.weeks||[]).reduce((n,w)=>(w.days||[]).length+n,0);
          return(
            <>
              <p style={{fontSize:10,fontWeight:700,color:T.inkMuted,letterSpacing:"1px",textTransform:"uppercase",padding:"0 2px",marginBottom:10,marginTop:6,fontFamily:T.fontSans}}>Active</p>
              <div style={{background:T.progCardBg,borderRadius:18,overflow:"hidden",marginBottom:10}}>
                {/* Banner strip */}
                <div style={{background:T.tileBg,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:T.tileAccent,letterSpacing:"0.5px",fontFamily:T.fontSans}}>Active programme</div>
                    <div style={{fontSize:11,fontWeight:600,color:T.tileMuted,fontFamily:T.fontSans,marginTop:2}}>Week {week+1} · Day {day+1}</div>
                  </div>
                  <span style={{background:T.tileAccent,color:T.progBannerBg,fontSize:9,fontWeight:700,borderRadius:8,padding:"4px 8px",fontFamily:T.fontSans}}>ACTIVE</span>
                </div>
                {/* Body */}
                <div style={{padding:"14px 16px 16px"}}>
                  <p style={{margin:"0 0 4px",fontSize:22,fontFamily:T.fontDisplay,fontWeight:800,color:"#fff"}}>{p.name.replace(/^Week \d+:\s*/i,"")}</p>
                  <p style={{margin:"0 0 14px",fontSize:11,color:"rgba(255,255,255,0.40)",fontFamily:T.fontSans,fontWeight:500}}>
                    {(p.weeks||[]).length} weeks · {totalDays} sessions
                  </p>
                  {/* Week pills */}
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
                    {(p.weeks||[]).map((_,wi)=>(
                      <span key={wi} style={{
                        background: wi===week ? T.tileAccent : wi<week ? (T.tileAccent+'33') : "rgba(255,255,255,0.07)",
                        color: wi===week ? T.progBannerBg : wi<week ? "rgba(255,255,255,0.40)" : "rgba(255,255,255,0.30)",
                        borderRadius:20,padding:"4px 10px",fontSize:10,fontWeight:700,fontFamily:T.fontSans,
                      }}>Wk {wi+1}</span>
                    ))}
                  </div>
                  {/* Divider */}
                  <div style={{height:1,background:"rgba(255,255,255,0.07)",marginBottom:14}}/>
                  {/* Actions */}
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <button onClick={()=>onSetActive(null)} style={{background:"rgba(255,255,255,0.07)",border:"none",borderRadius:T.radiusSm,padding:"10px 14px",cursor:"pointer"}}>
                      <span style={{fontSize:11,fontWeight:600,color:"rgba(255,255,255,0.45)",fontFamily:T.fontSans}}>Deactivate</span>
                    </button>
                    <button onClick={()=>openBuilder(p)} style={{flex:1,background:T.orange,border:"none",borderRadius:T.radiusSm,padding:"10px 14px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                      <PencilSimple size={13} weight="bold" color="#fff"/>
                      <span style={{fontSize:11,fontWeight:700,color:"#fff",fontFamily:T.fontSans}}>Edit plan</span>
                    </button>
                    <button onClick={()=>onDeleteProgramme(p.id)} style={{width:36,height:36,background:"rgba(239,68,68,0.15)",border:`1px solid ${T.red}`,borderRadius:T.radiusSm,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <Trash size={14} weight="bold" color={T.red}/>
                    </button>
                  </div>
                </div>
              </div>
            </>
          );
        })()}

        {/* Build your training — suggestion cards shown when active prog but no inactive ones */}
        {activeProg&&inactiveProgs.length===0&&(
          <div style={{marginBottom:14}}>
            <p style={{fontSize:10,fontWeight:700,color:T.inkMuted,letterSpacing:"1px",padding:"0 2px",marginBottom:10,fontFamily:T.fontSans}}>Build your training</p>
            {[
              {icon:<Sparkle size={18} color={T.orange}/>,label:"Generate an AI programme",sub:"Tell us your goals and we'll build a plan",action:()=>{setShowAiSetup(true);setAiGenError("");}},
              {icon:<CalendarBlank size={18} color={T.primary}/>,label:"Browse your routines",sub:"Start from your existing routines",action:()=>onNavigate&&onNavigate("routines")},
              {icon:<Plus size={18} color={T.inkMuted}/>,label:"Create from scratch",sub:"Build a custom programme manually",action:()=>openBuilder(null)},
            ].map((item,i)=>(
              <button key={i} onClick={item.action} style={{width:"100%",background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm,padding:"12px 14px",marginBottom:6,cursor:"pointer",display:"flex",alignItems:"center",gap:12,textAlign:"left"}}>
                <div style={{width:36,height:36,borderRadius:T.radiusSm,background:T.surfaceLight,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {item.icon}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{margin:0,fontSize:13,fontFamily:T.fontSans,fontWeight:700,color:T.inkDark}}>{item.label}</p>
                  <p style={{margin:"2px 0 0",fontSize:11,fontFamily:T.fontSans,color:T.inkMuted}}>{item.sub}</p>
                </div>
                <CaretRight size={14} color={T.inkFaint||T.inkMuted}/>
              </button>
            ))}
          </div>
        )}

        {/* Inactive programmes */}
        {inactiveProgs.length>0&&(
          <>
            <p style={{fontSize:10,fontWeight:700,color:T.inkMuted,letterSpacing:"1px",textTransform:"uppercase",padding:"0 2px",marginBottom:10,marginTop:6,fontFamily:T.fontSans}}>Previous plans</p>
            {inactiveProgs.map(p=>{
              const totalDays=(p.weeks||[]).reduce((n,w)=>(w.days||[]).length+n,0);
              return(
                <div key={p.id} style={{background:T.card,borderRadius:18,border:`1px solid ${T.cardBorder}`,overflow:"hidden",marginBottom:8}}>
                  {/* Dark header */}
                  <div style={{background:T.tileBg,padding:"11px 16px"}}>
                    <p style={{margin:0,fontSize:16,fontFamily:T.fontDisplay,fontWeight:800,color:"#fff"}}>{p.name.replace(/^Week \d+:\s*/i,"")}</p>
                    <p style={{margin:"2px 0 0",fontSize:10,color:"rgba(255,255,255,0.35)",fontFamily:T.fontSans,fontWeight:500}}>{(p.weeks||[]).length} weeks · {totalDays} sessions</p>
                  </div>
                  {/* Body */}
                  <div style={{padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={()=>onSetActive(p.id)} style={{background:T.surfaceLight,border:"none",borderRadius:T.radiusSm,padding:"8px 14px",cursor:"pointer"}}>
                        <span style={{fontSize:11,fontWeight:600,color:T.primary,fontFamily:T.fontSans}}>Set active</span>
                      </button>
                      <button onClick={()=>openBuilder(p)} style={{background:T.surfaceLight,border:"none",borderRadius:T.radiusSm,padding:"8px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
                        <PencilSimple size={12} weight="bold" color={T.inkMuted}/>
                        <span style={{fontSize:11,fontWeight:600,color:T.inkMuted,fontFamily:T.fontSans}}>Edit</span>
                      </button>
                    </div>
                    <button onClick={()=>onDeleteProgramme(p.id)} style={{width:36,height:36,background:"rgba(239,68,68,0.15)",border:`1px solid ${T.red}`,borderRadius:T.radiusSm,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <Trash size={14} weight="bold" color={T.red}/>
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* ── AI Setup Modal ─────────────────────────────────────────── */}
      {showAiSetup&&(
        <div style={{position:"fixed",top:0,left:0,width:"100%",height:"100%",background:T.backdrop,display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:T.card,borderRadius:24,width:"90%",maxWidth:360,overflow:"hidden"}}>
            <div style={{background:T.tileBg,padding:"14px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:18,fontFamily:T.fontDisplay,fontWeight:800,color:"#fff"}}>AI Programme Setup</span>
              <button onClick={()=>setShowAiSetup(false)} style={{background:"rgba(255,255,255,0.10)",border:"none",cursor:"pointer",color:"rgba(255,255,255,0.5)",padding:6,display:"flex",borderRadius:"50%",width:28,height:28,alignItems:"center",justifyContent:"center"}}><X size={14} weight="bold"/></button>
            </div>
            <div style={{padding:"16px 16px 20px"}}>
              {/* Goal */}
              <p style={{margin:"0 0 8px",fontSize:10,fontWeight:700,color:T.inkMuted,textTransform:"uppercase",letterSpacing:"1px",fontFamily:T.fontSans}}>Goal</p>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
                {STANDARD_GOALS.map(g=>(
                  <button key={g} onClick={()=>setAiGoal(g)} style={{background:aiGoal===g?T.primary:T.surfaceLight,color:aiGoal===g?"#fff":T.inkMuted,border:aiGoal===g?"none":`1px solid ${T.cardBorder}`,padding:"8px 16px",fontSize:12,fontFamily:T.fontSans,fontWeight:600,cursor:"pointer",borderRadius:20}}>{g}</button>
                ))}
                {hasPhysique&&(
                  <button onClick={()=>setAiGoal(CUSTOM_GOAL_KEY)} style={{background:aiGoal===CUSTOM_GOAL_KEY?T.primary:T.surfaceLight,color:aiGoal===CUSTOM_GOAL_KEY?"#fff":T.inkMuted,border:aiGoal===CUSTOM_GOAL_KEY?"none":`1px solid ${T.cardBorder}`,padding:"8px 16px",fontSize:12,fontFamily:T.fontSans,fontWeight:600,cursor:"pointer",borderRadius:20}}>
                    {profile.physique_goal.length>30?profile.physique_goal.slice(0,30)+"…":profile.physique_goal}
                  </button>
                )}
              </div>
              {/* Days per week */}
              <p style={{margin:"0 0 8px",fontSize:10,fontWeight:700,color:T.inkMuted,textTransform:"uppercase",letterSpacing:"1px",fontFamily:T.fontSans}}>Days per week</p>
              <div style={{display:"flex",gap:6,marginBottom:16}}>
                {[3,4,5,6].map(d=>(
                  <button key={d} onClick={()=>setAiDays(d)} style={{background:aiDays===d?T.primary:T.surfaceLight,color:aiDays===d?"#fff":T.inkMuted,border:aiDays===d?"none":`1px solid ${T.cardBorder}`,padding:"8px 18px",fontSize:13,fontFamily:T.fontSans,fontWeight:600,cursor:"pointer",borderRadius:20}}>{d}</button>
                ))}
              </div>
              {/* Equipment */}
              <p style={{margin:"0 0 8px",fontSize:10,fontWeight:700,color:T.inkMuted,textTransform:"uppercase",letterSpacing:"1px",fontFamily:T.fontSans}}>Equipment</p>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
                {["Full gym","Dumbbells & bodyweight","Bodyweight only"].map(eq=>(
                  <button key={eq} onClick={()=>setAiEquipment(eq)} style={{background:aiEquipment===eq?T.primary:T.surfaceLight,color:aiEquipment===eq?"#fff":T.inkMuted,border:aiEquipment===eq?"none":`1px solid ${T.cardBorder}`,padding:"8px 16px",fontSize:12,fontFamily:T.fontSans,fontWeight:600,cursor:"pointer",borderRadius:20}}>{eq}</button>
                ))}
              </div>
              {/* Time available */}
              <p style={{margin:"0 0 8px",fontSize:10,fontWeight:700,color:T.inkMuted,textTransform:"uppercase",letterSpacing:"1px",fontFamily:T.fontSans}}>Time available</p>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
                {["30 mins","45 mins","60 mins","90 mins"].map(t=>(
                  <button key={t} onClick={()=>setAiTime(t)} style={{background:aiTime===t?T.primary:T.surfaceLight,color:aiTime===t?"#fff":T.inkMuted,border:aiTime===t?"none":`1px solid ${T.cardBorder}`,padding:"8px 16px",fontSize:12,fontFamily:T.fontSans,fontWeight:600,cursor:"pointer",borderRadius:20}}>{t}</button>
                ))}
              </div>
              {/* Additional instructions */}
              <p style={{margin:"0 0 8px",fontSize:10,fontWeight:700,color:T.inkMuted,textTransform:"uppercase",letterSpacing:"1px",fontFamily:T.fontSans}}>Additional instructions <span style={{fontWeight:500,textTransform:"none"}}>(optional)</span></p>
              <textarea value={additionalInstructions} onChange={e=>setAdditionalInstructions(e.target.value)} maxLength={300}
                placeholder="e.g. Include farmer's walks, no front squats, prefer dumbbells for pressing"
                style={{width:"100%",background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:12,color:T.inkDark,fontFamily:T.fontSans,fontSize:12,padding:12,outline:"none",resize:"vertical",minHeight:48,maxHeight:100,boxSizing:"border-box",marginBottom:16}}/>
              {/* Weeks reminder */}
              <p style={{margin:"0 0 14px",fontSize:10,fontFamily:T.fontSans,color:T.inkMuted,fontWeight:500}}>Programme length: {numWeeks} weeks (change on the previous screen)</p>
              {aiSaveProgress&&<p style={{margin:"0 0 8px",fontSize:11,fontFamily:T.fontSans,color:T.primary,fontWeight:700}}>{aiSaveProgress}</p>}
              {aiGenError&&<p style={{margin:"0 0 8px",fontSize:11,fontFamily:T.fontSans,color:T.red,fontWeight:700}}>{aiGenError}</p>}
              <button onClick={generateWeeklyProgramme} disabled={aiGenLoading||!aiGoal||!aiDays||!aiEquipment||!aiTime}
                style={{width:"100%",background:T.orange,border:"none",borderRadius:T.radiusSm,padding:14,cursor:aiGenLoading||!aiGoal||!aiDays||!aiEquipment||!aiTime?"default":"pointer",opacity:aiGenLoading||!aiGoal||!aiDays||!aiEquipment||!aiTime?0.5:1}}>
                <span style={{fontSize:15,fontWeight:800,color:"#fff",fontFamily:T.fontDisplay,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                  {aiGenLoading?<><Spinner size={14}/> Generating...</>:<><Sparkle size={16} weight="bold"/> Generate programme</>}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
